'use client';

import { NextLink } from 'components/NextLink';
import RecipeDetail from 'components/RecipeDetail';
import RelatedRecipeComp from 'components/RelatedRecipes';
import { SocialSharingIcon } from 'components/SocialSharingIcon';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import React, { useEffect, useState } from 'react';
import { DictionaryService } from 'service/dictionary/dictionaryService';
import { ImagesService } from 'service/images/imagesService';

import type { IBreadcrumbTitles } from 'components/Breadcrumbs';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import type {
  HeroBannerImages,
  RecipeData,
  RecipeRating,
  RecipeResult,
  TipsAndTricksDetail,
} from 'types/Recipes';
import type { ISocialMediaListComponents } from 'types/SocialSharing';
import type { TipsAndTricksCMSData } from 'types/TipsAndTricksCMSData';

import { useScrollPercentageTracking } from '@/hooks/useScrollPercentageTracking';
import { useTimerScrollTracking } from '@/hooks/useTimerScrollTracking';
import axios from 'axios';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import { RelatedDataService } from 'service/relatedData/relatedDataService';
import { TrackingService } from 'service/tracking/trackingService';
import { GridBannerGap, MainTitle, SubTitle } from './PageLevelLayouts/Layouts';
import HappyCartScript from '@/components/HappyCart/HappyCartScript';
import HappyCartButton from '@/components/HappyCart/HappyCartButton';
import { Environment } from '@/config/environment';
import HappyCartDataJson from '@/components/HappyCart/HappyCartDataJson';
import { RECIPE_CONVERSION_TIMING_AFTER_SEARCH } from '@/config/config';
import { YouTubeService } from '@/service/google/youTubeService';

const Grid = dynamic(() => import('components/Grid').then((m: any) => m.Grid), {
  ssr: true,
}) as any;

const GridRow = dynamic(() => import('components/Grid').then((m: any) => m.GridRow), {
  ssr: true,
}) as any;

const Breadcrumbs = dynamic(
  () => import('components/Breadcrumbs').then((m: any) => m.Breadcrumbs),
  { ssr: true },
) as any;

const DownArrow = dynamic(() => import('components/DownArrow').then((m: any) => m.DownArrow), {
  ssr: true,
}) as any;

const RatingBanner = dynamic(
  () => import('components/RatingBanner').then((m: any) => m.RatingBanner),
  { ssr: true },
) as any;

const RecipeNutrition = dynamic(
  () => import('components/RecipeNutrition').then((m: any) => m.RecipeNutrition),
  { ssr: true },
) as any;

const RecipeOverview = dynamic(
  () => import('components/RecipeOverview').then((m: any) => m.RecipeOverview),
  { ssr: true },
) as any;

const HeroBannerRDP = dynamic(
  () => import('components/HeroBannerRDP').then((m: any) => m.HeroBannerRDP),
  { ssr: true },
) as any;

const SocialMedia = dynamic(() => import('components/SocialMedia').then((m) => m.SocialMedia), {
  ssr: true,
});

const UpArrow = dynamic(() => import('components/UpArrow').then((m: any) => m.UpArrow), {
  ssr: true,
}) as any;

const TipsAndTricks = dynamic(
  () => import('components/TipsAndTricks').then((m: any) => m.TipsAndTricks),
  { ssr: true },
) as any;

const SustainabilityBanner = dynamic(
  () => import('components/SustainabilityBanner').then((m: any) => m.SustainabilityBanner),
  { ssr: true },
) as any;

const TeaserLarge = dynamic(() => import('components/TeaserLarge').then((m) => m.TeaserLarge), {
  ssr: true,
});

const TeaserSmall = dynamic(() => import('components/TeaserSmall').then((m) => m.TeaserSmall), {
  ssr: true,
});
// React logic
const ClientRecipesSlugPage = ({
  breadcrumbsCMSData,
  recipe,
  recipeData,
  relatedRecipes,
  socialShareData,
  tipsAndTricksData,
  tipsAndTricksCardData,
  recipeCategorySlug,
  nutritionDictionary,
  locale,
  teasersLarge,
  teasersSmall,
  slug,
}: {
  breadcrumbsCMSData: IBreadcrumbTitles;
  locale: string;
  recipe: RecipeResult;
  recipeData: RecipeData;
  relatedRecipes: RecipeResult[];
  socialShareData: ISocialMediaListComponents[];
  tipsAndTricksData: TipsAndTricksCMSData;
  tipsAndTricksCardData: TipsAndTricksDetail[];
  recipeCategorySlug: string;
  nutritionDictionary: DictionaryItem[];
  teasersLarge: any;
  teasersSmall: any;
  slug: string;
}): React.ReactNode => {
  const { pathTranslations, userToken } = useGlobalContext();
  const [loaded, setLoaded] = useState(true);
  const [recipeRating, setRecipeRating] = useState<RecipeRating>(recipe?.ratingData);
  const [isRatingGiven, setIsRatingGiven] = useState(false);
  const [ratingGiven, setRatingGiven] = useState(0);
  const [comment, setComment] = useState<string>('');

  const { NEXT_PUBLIC_HAPPY_CART_CONFIG } = Environment.getBuildtimeEnvVars();
  const happyCartConfig =
    NEXT_PUBLIC_HAPPY_CART_CONFIG && JSON.parse(NEXT_PUBLIC_HAPPY_CART_CONFIG);

  useTimerScrollTracking({
    timeOut: 30 * 1000, // 30 seconds
    scrollPercentage: 25,
    eventCb: TrackingService.recipePageTimeScrollPercentage,
  });

  useTimerScrollTracking({
    timeOut: 5 * 60 * 1000, // 5 minutes
    scrollPercentage: 25,
    eventCb: TrackingService.recipePageTime,
  });

  useScrollPercentageTracking({
    percentageValues: [25, 50, 75, 100],
    eventCb: TrackingService.recipePageScroll,
  });

  const showHappyCart = !!recipeData.showHappyCartButton;
  /**
   * Recipes dictionary
   */
  const dictionary = recipeData?.titles?.dictionaryItems;

  const recipeOverviewTitles: any = {
    ratings: DictionaryService.getCopy('ratings', dictionary),
    print: DictionaryService.getCopy('print', dictionary),
    share: DictionaryService.getCopy('share', dictionary),
    readMore: DictionaryService.getCopy('read_more', dictionary),
    readLess: DictionaryService.getCopy('read_less', dictionary),
    difficulty: {
      0: DictionaryService.getCopy('easy', dictionary),
      1: DictionaryService.getCopy('intermediate', dictionary),
      2: DictionaryService.getCopy('tough', dictionary),
    },
    cost: {
      0: DictionaryService.getCopy('inexpensive', dictionary),
      1: DictionaryService.getCopy('normal', dictionary),
      2: DictionaryService.getCopy('expensive', dictionary),
    },
  };
  const difficulty = recipeOverviewTitles.difficulty[recipe?.Difficulty];

  const getRecipeTags = () => {
    const tags: any[] = [];
    recipe?.DietTypes?.map((item: { Title: any; GlobalDietType: any }) =>
      tags.push({
        title: item?.Title,
        url: `${pathTranslations.search}?filters=${encodeURIComponent(
          `diet_types:${item?.Title}`,
        )}&category=recipes`,
        id: item?.GlobalDietType?.Title || '',
      }),
    );
    return tags;
  };

  useEffect(() => {
    // Tracking
    if (recipe) {
      TrackingService.contentGroups({
        cg1: 'recipes',
        cg5: recipe?.Category?.GlobalCategory?.Title || '',
      });
    }

    // Loaded state
    setLoaded(true);
    TrackingService.recipePage(difficulty, getRecipeTags(), recipe, recipe?.ratingData);

    const timeout = setTimeout(() => {
      TrackingService.recipeDetailPageConversionAfterSearch(
        (window.dataLayer as Record<string, string>[]) || [],
        userToken,
      );
    }, RECIPE_CONVERSION_TIMING_AFTER_SEARCH);

    return () => {
      setRecipeRating({ recipeId: '', amount: 0, value: 0 });
      setLoaded(false);
      clearTimeout(timeout);
    };
  }, []);

  /**
   * Hero Banner Images
   */
  const recipeImages: HeroBannerImages = ImagesService.getRecipeImageForHeroBanner(recipe);

  const sharableImage = recipeImages.desktop || recipeImages.mobile;

  const ratingsData = {
    rating_en: DictionaryService.getCopy('ratings', dictionary),
    rating_subtitle: DictionaryService.getCopy('rating_subtitle', dictionary),
    rating_title: DictionaryService.getCopy('rating_title', dictionary),
    rate_this: DictionaryService.getCopy('rate_this', dictionary),
    ratingThanks: DictionaryService.getCopy('ratingThanks', dictionary),
    ratingBtn: DictionaryService.getCopy('ratingBtn', dictionary),
    rating_body: DictionaryService.getCopy('rating_body', dictionary),
    videoLabel: DictionaryService.getCopy('watch_video', dictionary),
    rating_comment_label: DictionaryService.getCopy('rating_comment_label', dictionary),
    rating_comment_error: DictionaryService.getCopy('rating_comment_error', dictionary),
    rating_banner_text: recipeData.textBlockForRatingBannerInPreparationColumn,
  };

  const relatedRecipesTitles = {
    seeMore: DictionaryService.getCopy('see_recipe_details', dictionary),
    relatedRecipesSubTitle: DictionaryService.getCopy('related_recipe', dictionary),
    relatedRecipesTitle: DictionaryService.getCopy('related_recipetitle', dictionary),
  };
  const TotalTime =
    recipe && recipe?.TotalTimeInMinutes
      ? `${recipe?.TotalTimeInMinutes} ${DictionaryService.getCopy('minutes', dictionary)}`
      : '';
  const PreparationTime =
    recipe && recipe?.PreparationTimeInMinutesSummaryText
      ? `${recipe?.PreparationTimeInMinutesSummaryText}`
      : '';
  const recipeOverviewData = {
    title: recipe?.Title,
    rating: recipeRating,
    tags: getRecipeTags(),
    servings: recipe?.ServingsSummaryText,
    cost: recipe?.Price == null ? undefined : (recipe?.Price as any),
    difficulty: recipe?.Difficulty,
    preparationTime: TotalTime || PreparationTime,
    intro: recipe?.ShortDescription || '',
    summary: recipe?.IntroductionText || '',
    ratingMsg: recipeData?.ratingmsg,
    sharableImage,
  };
  const tipsLifestyleImage = recipeData?.recipelifestyle?.[0]?.asset?.[0];
  const upperRightImage = recipeData?.recipelifestyle?.[3]?.asset?.[0];

  let carouselVideoID;
  if (recipe?.VideoInstruction?.Video) {
    carouselVideoID = YouTubeService.getVideoId(
      recipe?.VideoInstruction?.Video.YouTubeVideoLink || '',
    );
  }

  let youTubeLink;
  if (recipe?.VideoInstruction?.Video) {
    youTubeLink = recipe?.VideoInstruction?.Video?.YouTubeVideoLink;
  }

  let keyword;
  recipe?.Keywords.forEach((kw: { Title: string }) => {
    keyword = kw?.Title || '';
  });

  let recipeCuisine;
  if (recipe?.NationalCuisine) {
    recipeCuisine = recipe?.NationalCuisine || '';
  }

  const breadcrumbsCategory = [
    {
      slug: pathTranslations.recipes,
      displayName: breadcrumbsCMSData?.recipesText?.value,
    },
  ];
  if (recipeCategorySlug) {
    breadcrumbsCategory.push({
      slug: `${pathTranslations.recipesCategory}${recipeCategorySlug}`,
      displayName: recipe?.Category?.Title || '',
    });
  }

  const settingRateData = {
    isRatingGiven,
    setIsRatingGiven,
    ratingGiven,
    setRatingGiven,
    comment,
    setComment,
  };

  useEffect(() => {
    if (ratingGiven > 0) {
      const ratingSubmit = async () => {
        if (recipe && recipe.Id) {
          const response = await axios.post(`/next-api/recipe-ratings/${recipe.Id}`, {
            rating: ratingGiven,
            locale,
          });

          if (response.status === 200) {
            const { recipeId, numberOfVotes, averageRatingScore } = response.data;

            setRecipeRating({
              recipeId: recipeId,
              amount: numberOfVotes || 0,
              value: averageRatingScore || 0,
            });
          }
        }
      };
      ratingSubmit();
    }
  }, [ratingGiven]);

  const tipsCardData = RelatedDataService.getRelatedData(
    tipsAndTricksCardData,
    pathTranslations,
    'tipsAndTricks',
    tipsAndTricksData?.tipDetailsText?.value,
  );

  const nutritionIsActive = recipeData?.nutritionIsActive;

  const decimalSeparator =
    DictionaryService.getCopy('see_recipe_details', nutritionDictionary) || '.';

  return (
    <>
      {locale === 'nl_NL' && <Script async src="https://cdn.foodinfluencersunited.nl/r2b.js" />}

      {/* For initial data parsing, add the Happy cart data to the page independent of the status (showHappyCart) in the CMS */}
      {/* TODO: Move it to the condition below once the data are available on Happy cart side */}
      <HappyCartDataJson recipe={recipe} config={happyCartConfig} currentLocale={locale} />

      {showHappyCart && (
        <>
          <HappyCartScript currentLocale={locale} config={happyCartConfig} />
        </>
      )}

      <div
        itemScope
        itemType="https://schema.org/Recipe"
        className="max-w-6xl mx-auto print:hidden"
      >
        {/* // TODO: update author data once available */}
        <meta itemProp="author" content="Dr.&nbsp;Oetker" />
        <meta itemProp="image" content={sharableImage} />
        <div className="relative">
          <Breadcrumbs
            categories={breadcrumbsCategory}
            titles={breadcrumbsCMSData}
            pageName={recipe?.Title}
          />
        </div>
        <div className="absolute z-10 right-5 hidden md:block top-20 md:top-11" data-print="hide">
          <SocialSharingIcon
            socialMediaListComponents={socialShareData}
            sharedItemName={recipeOverviewData.title}
            sharedItemImage={sharableImage}
          />
        </div>
        <>
          <HeroBannerRDP
            image={recipeImages}
            mobileImage={recipeImages}
            video={youTubeLink}
            videoData={carouselVideoID}
            keywords={keyword}
            recipeCategory={recipeCategorySlug}
            recipeCuisine={recipeCuisine}
          />
          <DownArrow />
        </>
        <Grid>
          <div className="z-10 relative">
            <RecipeOverview
              data={recipeOverviewData}
              copy={ratingsData}
              titles={recipeOverviewTitles}
              socialMediaListComponents={socialShareData}
              settingRateData={settingRateData}
              videoId={carouselVideoID}
              trackingEvents={{
                clickSocialShare: TrackingService.clickSocialShare,
                clickRatingRecipe: TrackingService.clickRatingRecipe,
                clickPrintRecipe: TrackingService.clickPrintRecipe,
              }}
              showCommentField={recipeData.showCommentFieldInRatingModal}
              showContactPageData={recipeData.showContactPageData}
              contactPageData={recipeData.contactPageData?.html || ''}
            />
          </div>
        </Grid>
        <RecipeDetail
          recipe={recipe}
          copy={recipeData?.titles?.dictionaryItems}
          tipslifeStyle={tipsLifestyleImage}
          upperRightImage={upperRightImage}
          ratingData={ratingsData}
          ratingMsg={recipeOverviewData.ratingMsg}
          settingRateData={settingRateData}
          recipeTitle={recipe?.Title ?? ''}
          showCommentField={recipeData.showCommentFieldInRatingModal}
          showContactPageData={recipeData.showContactPageData}
          contactPageData={recipeData.contactPageData?.html || ''}
          showRatingBanner={recipeData.showRatingBannerInPreparationColumn}
          hideTabsOnMobile={recipeData.hideTabsOnMobile}
        />
        {/* Recipe Nutrition */}
        {nutritionIsActive && (
          <Grid>
            {recipe?.NutritionalInformationPer100g &&
              recipe?.NutritionalInformationPerPortion &&
              recipeData?.titles?.dictionaryItems && (
                <GridRow>
                  {loaded && (
                    <RecipeNutrition
                      decimalSeparator={decimalSeparator}
                      data={[
                        recipe.NutritionalInformationPer100g,
                        recipe.NutritionalInformationPerPortion,
                      ]}
                      serving={String(recipe?.ServingWeightInGramsPerPortion)}
                      titles={recipeData?.titles?.dictionaryItems}
                    />
                  )}
                </GridRow>
              )}
          </Grid>
        )}
        {/* Rating */}
        {ratingsData &&
          recipeOverviewData.ratingMsg &&
          settingRateData &&
          !recipeData.showRatingBannerInPreparationColumn && (
            <div data-print="hide">
              <Grid skipPaddingOnMobile>
                <GridRow>
                  <GridBannerGap>
                    <RatingBanner
                      ratingData={ratingsData}
                      ratingMsg={recipeOverviewData.ratingMsg}
                      settingRateData={settingRateData}
                      trackingEvent={TrackingService.clickRatingRecipe}
                      recipeTitle={recipe?.Title ?? ''}
                      showCommentField={recipeData.showCommentFieldInRatingModal}
                      showContactPageData={recipeData.showContactPageData}
                      contactPageData={recipeData.contactPageData?.html || ''}
                    />
                  </GridBannerGap>
                </GridRow>
              </Grid>
            </div>
          )}
        <Grid>
          {/* Tips & Tricks */}
          {tipsAndTricksData?.introTexts?.[0] && tipsCardData && !!tipsCardData?.length && (
            <GridBannerGap>
              <TipsAndTricks
                introText={tipsAndTricksData?.introTexts?.[0]}
                recipes={tipsAndTricksData?.recipes}
                cardsData={tipsCardData}
                difficultyCopy=""
                LinkComponent={NextLink}
              />
            </GridBannerGap>
          )}
          {/* Associated Recipes */}
          {recipe?.AssociatedRecipes && recipe?.AssociatedRecipes.length > 0 && (
            <>
              <GridRow>
                <SubTitle>{DictionaryService.getCopy('related_recipes', dictionary)}</SubTitle>
                <MainTitle>
                  {DictionaryService.getCopy('more_delicious_ideas_for_you', dictionary)}
                </MainTitle>
              </GridRow>
              <GridRow>
                {recipe?.AssociatedRecipes.map((related: any) => (
                  <div key={related?.Id}>
                    <NextLink href={`/recipes/${related?.Id}`}>{related?.Title}</NextLink>
                  </div>
                ))}
              </GridRow>
            </>
          )}
        </Grid>
        {teasersLarge && <TeaserLarge teasers={teasersLarge} />}
        <RelatedRecipeComp
          currentRecipe={recipe}
          linkName={relatedRecipesTitles?.seeMore}
          relatedRecipe={relatedRecipes?.slice(0, 9) ?? []}
          copy={recipeOverviewTitles?.difficulty}
          subTitle={relatedRecipesTitles?.relatedRecipesSubTitle}
          title={relatedRecipesTitles?.relatedRecipesTitle}
        />
        {recipeData?.sustainabilityBanner && (
          <SustainabilityBanner {...recipeData?.sustainabilityBanner} />
        )}
        {teasersSmall && <TeaserSmall teasersSmall={teasersSmall} />}
        {!!recipeData?.genericFollowUs && (
          <SocialMedia
            id={recipeData.genericFollowUs.id}
            super={recipeData.genericFollowUs.super}
            title={recipeData.genericFollowUs.title}
            socialMedia={recipeData.genericFollowUs.socialMedia}
            middlegroundImage={recipeData.genericFollowUs.middlegroundImage}
            foregroundImage={recipeData.genericFollowUs.foregroundImage}
          />
        )}
      </div>
      <UpArrow />

      {showHappyCart && (
        <HappyCartButton
          config={happyCartConfig}
          slug={slug}
          servingSize={recipe?.FromServings || recipe?.ToServings}
          recipeId={recipe.Id}
          currentLocale={locale}
          key={recipe.Id}
        />
      )}
    </>
  );
};

export default ClientRecipesSlugPage;
